'use client';

import React, { useState, useRef, useEffect, useId } from 'react';
import { Plus, Tag } from 'lucide-react';
import { clsx } from 'clsx';
import { TagAssignContent } from './tag-assign-content';
import { useFavorites } from './use-favorites';

interface FavoriteLabelPopoverProps {
  articleNumber: string;
  currentLabels: string[];
  /** Position-Hint für bessere Platzierung. */
  align?: 'left' | 'right';
  /** Optionaler eigener Trigger. */
  trigger?: React.ReactNode;
}

/**
 * Farb-bewusster Label-Popover für die Favoriten-Karte.
 *
 * Self-wired über `useFavorites`: rendert das geteilte `TagAssignContent`
 * (Farb-Chips + Anlegen) in einem adaptiv platzierten Panel. Mirror der
 * Open/Close/Outside-Click/Escape-Mechanik der alten `LabelPopover`.
 */
export function FavoriteLabelPopover({
  articleNumber,
  currentLabels,
  align = 'left',
  trigger,
}: FavoriteLabelPopoverProps) {
  const { labels, toggleLabelOnArticle, addUserLabel } = useFavorites();
  const [open, setOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  // Außerhalb-Klick schließt.
  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  // Escape schließt.
  useEffect(() => {
    if (!open) return;
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [open]);

  // Beim Öffnen Richtung bestimmen (genug Platz nach unten?).
  useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUpward(spaceBelow < 320);
    }
  }, [open]);

  return (
    <div
      ref={containerRef}
      className="relative inline-block"
      onClick={(e) => e.stopPropagation()}
    >
      <div onClick={() => setOpen((v) => !v)} className="cursor-pointer">
        {trigger ?? (
          <button
            type="button"
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-controls={panelId}
            className={clsx(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10.5px] font-bold uppercase tracking-wide transition-all cursor-pointer border border-dashed min-h-[24px]',
              open
                ? 'bg-primary/5 text-primary border-primary/50'
                : 'bg-gray-50 text-gray-400 border-gray-300 hover:border-primary/50 hover:text-primary hover:bg-primary/5',
            )}
            title="Label vergeben"
          >
            <Plus size={11} strokeWidth={3} />
            <Tag size={10} />
            <span>Label</span>
          </button>
        )}
      </div>

      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-label="Label zuweisen"
          className={clsx(
            'absolute w-72 max-w-[calc(100vw-2rem)] max-h-[70dvh] overflow-y-auto bg-white rounded-xl shadow-xl border border-gray-200 z-50 animate-in fade-in zoom-in-95 duration-150',
            openUpward ? 'bottom-full mb-1' : 'top-full mt-1',
            align === 'right' ? 'right-0' : 'left-0',
          )}
        >
          <TagAssignContent
            labels={labels}
            activeLabels={currentLabels}
            onToggle={(l) => toggleLabelOnArticle(articleNumber, l)}
            onCreate={(name, color) =>
              addUserLabel(name, color).then(() =>
                toggleLabelOnArticle(articleNumber, name),
              )
            }
          />
        </div>
      )}
    </div>
  );
}
